/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { Chips } from 'primereact/chips';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { AntflowApiEndpoint, AntFlowServerError } from '@/services/dataprovider';
import { LauncherCreated } from '@/types/models';
import { TokenDisplay } from './TokenDisplay';
import { UserGroupsBanner } from './UserGroupsBanner';

interface CreateLauncherDialogProps {
  visible: boolean;
  onHide: () => void;
  onSuccess: () => void;
  apiEndpoint: AntflowApiEndpoint;
}

export const CreateLauncherDialog: React.FC<CreateLauncherDialogProps> = ({
  visible,
  onHide,
  onSuccess,
  apiEndpoint
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [groups, setGroups] = useState<string[] | null>([]);
  const [isTransverse, setIsTransverse] = useState(false);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [createdLauncher, setCreatedLauncher] = useState<LauncherCreated | null>(null);
  const [error, setError] = useState<string | null>();

  const createMutation = useMutation({
    mutationFn: async () => {
      const expDate = expirationDate ? expirationDate.toISOString() : null;
      return apiEndpoint.add_launcher({
        name,
        groups: isTransverse ? groups : null,
        is_transverse: isTransverse,
        expiration_date: expDate
      });
    },
    onSuccess: (data) => {
      setCreatedLauncher(data);
      onSuccess();
    },
    onError(error: AntFlowServerError) {
      const errorData = error.response?.data as Record<string, string>;
      if (typeof errorData === 'object' && errorData !== null) {
        setError(errorData.detail);
      }
    }
  });

  const handleClose = () => {
    setName('');
    setGroups([]);
    setIsTransverse(false);
    setExpirationDate(null);
    setCreatedLauncher(null);
    setError(null);
    createMutation.reset();
    onHide();
  };

  const handleSubmit = () => {
    if (name.trim()) {
      createMutation.mutate();
    }
  };

  const footer = createdLauncher ? (
    <Button label={t(['actions.close', 'Close'])} onClick={handleClose} />
  ) : (
    <div className="flex justify-content-end gap-2">
      <Button
        label={t(['actions.cancel', 'Cancel'])}
        severity="secondary"
        size="small"
        onClick={handleClose}
        disabled={createMutation.isPending}
      />
      <Button
        label={t(['launchers.create', 'Create'])}
        icon="pi pi-plus"
        size="small"
        onClick={handleSubmit}
        disabled={!name.trim() || createMutation.isPending}
        loading={createMutation.isPending}
      />
    </div>
  );

  return (
    <Dialog
      header={t(['launchers.create_title', 'Create New Launcher'])}
      visible={visible}
      style={{ width: '40%' }}
      onHide={handleClose}
      footer={footer}
      closable={!createMutation.isPending}
    >
      {createdLauncher ? (
        <div className="flex flex-column gap-3">
          <div className="p-message p-message-success p-3 border-round">
            <i className="pi pi-check-circle mr-2" />
            {t(['launchers.created_success', 'Launcher created successfully!'])}
          </div>
          <TokenDisplay token={createdLauncher.token} label={t(['launchers.token', 'Token'])} />
        </div>
      ) : (
        <div className="flex flex-column gap-3">
          <UserGroupsBanner className="border-round surface-100 p-3" />

          <div className="flex flex-column gap-2">
            <label htmlFor="launcher-name" className="font-medium">
              {t(['launchers.name', 'Name'])} *
            </label>
            <InputText
              id="launcher-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t(['launchers.name_placeholder', 'Enter launcher name'])}
              disabled={createMutation.isPending}
            />
          </div>

          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="is-transverse"
              checked={isTransverse}
              onChange={(e) => setIsTransverse(e.checked ?? false)}
              disabled={createMutation.isPending}
            />
            <label htmlFor="is-transverse" className="cursor-pointer">
              {t(['launchers.is_transverse', 'Transverse launcher'])}
            </label>
          </div>

          {isTransverse && (
            <div className="flex flex-column gap-2">
              <label htmlFor="launcher-groups" className="font-medium">
                {t(['launchers.groups', 'Groups'])}
              </label>
              <Chips
                allowDuplicate={false}
                className="w-full"
                pt={{
                  container: { className: 'w-full' }
                }}
                id="launcher-groups"
                value={groups ?? []}
                onChange={(e) => setGroups(e.value ?? [])}
                placeholder={t(['launchers.groups_placeholder', 'Type and press Enter to add'])}
                disabled={createMutation.isPending}
              />
            </div>
          )}

          <div className="flex flex-column gap-2">
            <label htmlFor="expiration-date" className="font-medium">
              {t(['launchers.expiration_date', 'Expiration date'])}
              {!isTransverse && ' *'}
            </label>
            <Calendar
              id="expiration-date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.value as Date | null)}
              showIcon
              minDate={new Date()}
              disabled={createMutation.isPending}
            />
          </div>

          {createMutation.isError && (
            <div className="p-message p-message-error p-3 border-round">
              <i className="pi pi-times-circle mr-2" />
              {error || t(['notifs.error.description', 'An error occurred'])}
            </div>
          )}

          <div>
            <small className="text-sm text-500">{t(['misc.required_field', ''])}</small>
          </div>
        </div>
      )}
    </Dialog>
  );
};
